import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapPin, BellRing, CheckCircle2, Search } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import LeadCaptureForm from '@/components/marketing/LeadCaptureForm';
import { createStateAlert, fetchTopCities, TopCities } from '@/lib/stateAlerts';
import type { UsState } from '@/data/seo/states';
import { stateLabel, citySlug, hasCityPages, stateCityPath, CITY_PAGES_PER_STATE } from '@/data/seo/states';

/* ------------------------------------------------------------------ */
/* "Get alerts for new projects in {State}" / "{City}, {ST}"           */
/* ------------------------------------------------------------------ */
export const StateAlertForm: React.FC<{ st: UsState; city?: string }> = ({ st, city }) => {
  const { lang } = useI18n();
  const { profile } = useAuth() as unknown as { profile?: { id: string; email: string } | null };

  const [params] = useSearchParams();
  const stateName = stateLabel(st, lang);
  const name = city ? `${city}, ${st.code}` : stateName;
  const es = lang === 'es';
  const confirmed = params.get('alert') === 'confirmed';
  const tags = ['job-alert', 'state-page', `state:${st.slug}`];
  if (city) tags.push('city-page', `city:${citySlug(city)}-${st.slug}`);

  const onSubmitted = async ({ email, trades }: { email: string; trades: string[] }) => {
    const status = await createStateAlert({ profile, email, lang, state: st.code, stateName: st.en, city: city || null, trades });
    switch (status) {
      case 'active':
        return es ? `Listo. Tu alerta de ${name} está activa en tu panel; recibirás un correo diario cuando haya proyectos nuevos.` : `Done. Your ${name} alert is active in your dashboard — you'll get a daily email when new projects match.`;
      case 'already_active':
        return es ? `Ya tienes una alerta activa para ${name} con esos oficios.` : `You already have an active ${name} alert for those trades.`;
      case 'pending':
        return es ? `Revisa tu correo y confirma la alerta. No enviaremos nada hasta que la confirmes.` : `Check your inbox and confirm the alert. Nothing is sent until you confirm.`;
      case 'too_many':
        return es ? 'Llegaste al máximo de 5 alertas. Elimina una en tu panel para crear otra.' : 'You have reached the 5-alert limit. Remove one from your dashboard to add another.';
      case 'rate_limited':
        return es ? 'Demasiados intentos. Inténtalo de nuevo en una hora.' : 'Too many attempts. Please try again in an hour.';
      default:
        return es ? 'Te suscribimos a novedades, pero no pudimos crear la alerta. Inténtalo de nuevo.' : 'You are on our updates list, but the alert could not be created. Please try again.';
    }
  };

  return (
    <section id="alerts" className="mt-10 scroll-mt-24">
      {confirmed && (
        <p role="status" className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-800">
          <CheckCircle2 className="h-4 w-4" aria-hidden />
          {es ? `Alerta confirmada. Recibirás nuevos proyectos de ${name} por correo.` : `Alert confirmed. New ${name} projects will arrive by email.`}
        </p>
      )}
      <LeadCaptureForm
        audience="subcontractor"
        source={city ? 'state-city-page' : 'state-page'}
        state={st.code}
        city={city}
        tags={tags}
        multiTrade
        title={es ? `Recibe alertas de nuevos proyectos en ${name}` : `Get alerts for new projects in ${name}`}
        subtitle={
          es
            ? `Un correo diario solo cuando se publiquen proyectos nuevos en ${name} que coincidan con tus oficios. Sin proyectos, sin correo.`
            : `One daily email, only when new ${name} projects match your trades. No new projects, no email.`
        }
        submitLabel={es ? 'Crear alerta' : 'Create alert'}
        onSubmitted={onSubmitted}
      >
        <p className="mt-3 inline-flex flex-wrap items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700">
          <BellRing className="h-3.5 w-3.5" aria-hidden />
          {city && <>{es ? 'Ciudad' : 'City'}: {city} · </>}
          {es ? 'Estado' : 'State'}: {stateName} ({st.code})
        </p>
      </LeadCaptureForm>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Top cities — counted server-side (browse-listings `cities` mode)    */
/* Outside California each city opens its state+city landing page      */
/* (/states/tx/plano); the search icon keeps the direct marketplace     */
/* filter (/jobs?state=TX&city=Plano). `exclude` hides the current city */
/* when this block is reused on a city page as "Other top cities".     */
/* ------------------------------------------------------------------ */
export const TopCitiesSection: React.FC<{ st: UsState; exclude?: string; title?: string }> = ({ st, exclude, title }) => {
  const { lang, path } = useI18n();
  const [data, setData] = useState<TopCities | null | undefined>(undefined);
  const name = stateLabel(st, lang);
  const es = lang === 'es';
  const cityPages = hasCityPages(st);

  useEffect(() => {
    let alive = true;
    setData(undefined);
    fetchTopCities(st.code, CITY_PAGES_PER_STATE).then((d) => alive && setData(d));
    return () => {
      alive = false;
    };
  }, [st.code]);

  const cities = (data?.cities || []).filter((c) => !exclude || citySlug(c.city) !== citySlug(exclude));
  const browse = (city: string) => `${path('/jobs')}?state=${st.code}&city=${encodeURIComponent(city)}`;

  return (
    <section className="mt-10">
      <h2 className="text-xl font-black text-slate-900 sm:text-2xl">
        {title || (es ? `Ciudades con más proyectos abiertos en ${name}` : `Top cities in ${name} by open projects`)}
      </h2>
      {data === undefined ? (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl border border-slate-200 bg-slate-100" />
          ))}
        </div>
      ) : !data || cities.length === 0 ? (
        <p className="mt-3 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
          {es ? `Todavía no hay otras ciudades con proyectos abiertos en ${name}. Crea una alerta y te avisamos cuando se publique uno.` : `No other ${name} cities have open projects yet. Create an alert and we'll email you when one is posted.`}
        </p>
      ) : (
        <>
          {!exclude && (
            <p className="aeo-summary mt-2 text-sm text-slate-600">
              {es
                ? `${data.totalOpen} proyectos abiertos en ${data.citiesWithProjects} ciudades de ${name}. Actualizado ${new Date(data.generatedAt).toLocaleDateString('es-US')}.`
                : `${data.totalOpen} open projects across ${data.citiesWithProjects} ${name} cities. Updated ${new Date(data.generatedAt).toLocaleDateString('en-US')}.`}
            </p>
          )}
          <ol className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {cities.map((c, i) => (
              <li key={c.city} className="group flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-4 hover:border-orange-300 hover:shadow-sm">
                <Link to={cityPages ? path(stateCityPath(st, c.city)) : browse(c.city)} className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="text-xs font-black text-slate-400">{i + 1}</span>
                  <MapPin className="h-4 w-4 shrink-0 text-orange-500" aria-hidden />
                  <span className="truncate text-sm font-bold text-slate-900 group-hover:text-orange-600">{c.city}</span>
                </Link>
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">
                    {c.count} {es ? (c.count === 1 ? 'proyecto' : 'proyectos') : c.count === 1 ? 'project' : 'projects'}
                  </span>
                  {cityPages && (
                    <Link
                      to={browse(c.city)}
                      aria-label={es ? `Ver proyectos en ${c.city} en el marketplace` : `Browse ${c.city} projects in the marketplace`}
                      className="rounded-md p-1 text-slate-400 hover:bg-orange-50 hover:text-orange-600"
                    >
                      <Search className="h-3.5 w-3.5" aria-hidden />
                    </Link>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
};
